import { useState } from 'react';

interface AIPromptBarProps {
  onGenerate: (prompt: string) => void;
  isGenerating: boolean;
}

const templateSuggestions = [
  { icon: 'ri-shopping-bag-line', text: 'eCommerce store', color: 'text-teal-500' },
  { icon: 'ri-restaurant-line', text: 'Food delivery', color: 'text-orange-500' },
  { icon: 'ri-heart-pulse-line', text: 'Fitness tracker', color: 'text-red-500' },
  { icon: 'ri-calendar-check-line', text: 'Booking app', color: 'text-indigo-500' },
  { icon: 'ri-user-heart-line', text: 'Social network', color: 'text-pink-500' },
  { icon: 'ri-briefcase-line', text: 'Business dashboard', color: 'text-blue-500' },
];

export default function AIPromptBar({ onGenerate, isGenerating }: AIPromptBarProps) {
  const [prompt, setPrompt] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.trim() && !isGenerating) {
      onGenerate(prompt.trim());
      setPrompt('');
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setPrompt(suggestion);
    setShowSuggestions(false);
    onGenerate(suggestion);
  };

  return (
    <div className="relative w-full">
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all">
          <div className="pl-4 pr-2 flex items-center">
            <div className="w-6 h-6 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <i className="ri-magic-line text-white text-xs"></i>
            </div>
          </div>
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            placeholder="Describe your app... (e.g., 'fitness tracker with workout plans')"
            disabled={isGenerating}
            className="flex-1 bg-transparent border-none outline-none py-2.5 text-sm text-gray-800 placeholder-gray-500 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!prompt.trim() || isGenerating}
            className="px-4 py-2 m-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs font-bold rounded-lg hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1.5 whitespace-nowrap"
          >
            {isGenerating ? (
              <>
                <i className="ri-loader-4-line text-sm animate-spin"></i>
                Generating...
              </>
            ) : (
              <>
                <i className="ri-sparkling-line text-sm"></i>
                Generate
              </>
            )}
          </button>
        </div>
      </form>

      {/* Template Suggestions Dropdown */}
      {showSuggestions && !isGenerating && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowSuggestions(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-20">
            <div className="p-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                <i className="ri-lightbulb-line text-teal-500"></i>
                Quick Start Templates
              </p>
            </div>
            <div className="p-2 max-h-64 overflow-y-auto">
              {templateSuggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(suggestion.text)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer text-left"
                >
                  <i className={`${suggestion.icon} text-lg ${suggestion.color} w-5 h-5 flex items-center justify-center`}></i>
                  <span className="text-sm font-medium text-gray-700">{suggestion.text}</span>
                  <i className="ri-arrow-right-line text-gray-400 text-xs ml-auto"></i>
                </button>
              ))}
            </div>
            <div className="p-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Or describe your own custom app idea above
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}